import { has, isEqual, isNil, omit } from 'lodash/fp';
import pt from 'prop-types';
import { Component } from 'react';
import { LocalizedPropertyProps } from '../types';

interface CreateEntityLocalizerConfig<P> {
    subscribeChannelKey: string;
    handlerKey: string;
    displayName: string;
    entityName: keyof P;
}

const isLocalizedPropertyProps = (props: unknown): props is LocalizedPropertyProps => has('options', props);

export default <P extends {}>({
    subscribeChannelKey,
    handlerKey,
    displayName,
    entityName,
}: CreateEntityLocalizerConfig<P>) =>
    class EntityLocalizer extends Component<P> {
        static displayName = displayName;
        public unsubscribe?: () => void;

        declare context: {
            [key in typeof subscribeChannelKey | typeof handlerKey]: Function;
        };
        static contextTypes = {
            [subscribeChannelKey]: pt.func.isRequired,
            [handlerKey]: pt.func.isRequired,
        };

        shouldComponentUpdate = (nextProps: P) => !isEqual(nextProps, this.props);

        componentDidMount() {
            this.unsubscribe = this.context[subscribeChannelKey](() => this.forceUpdate());
        }

        componentWillUnmount() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }

        render() {
            const entity = this.props[entityName];

            if (isNil(entity)) {
                return null;
            }

            let { props } = this;
            const options = isLocalizedPropertyProps(props) ? props.options : undefined;

            if (isLocalizedPropertyProps(props)) {
                props = omit('options', props);
            }

            const { [entityName]: omittedProp, ...elementProps } = props;
            const localizedEntity = this.context[handlerKey](entity, options);

            return (
                <span data-localized-entity {...elementProps}>
                    {localizedEntity}
                </span>
            );
        }
    };
